import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { consentDisclosureApi, consentDisclosureKeys } from '@client/api/waterlily';

const FALLBACK_ORG = 'Your advisor’s organization';

function Disclaimer({ legalName }: { legalName: string }) {
  return (
    <p className="text-muted-foreground bg-muted mt-8 rounded-md border px-2 py-1.5 text-xs leading-relaxed">
      The entities and individuals listed on this page are third party business partners (“Business
      Partner”) of {legalName}. You understand and agree that this page may be periodically updated
      to add or remove Business Partners. To stay apprised of the most current list of Business
      Partners, it is your responsibility to visit this page.
    </p>
  );
}

export function ConsentDisclosurePage() {
  const { advisorId } = useParams<{ advisorId: string }>();

  const { data, isLoading, isError } = useQuery({
    queryKey: consentDisclosureKeys.detail(advisorId ?? ''),
    queryFn: () => consentDisclosureApi.get(advisorId as string),
    enabled: Boolean(advisorId),
  });

  const legalName = data?.orgLegal || data?.orgBrand || FALLBACK_ORG;
  const brandName = data?.orgBrand || data?.orgLegal || FALLBACK_ORG;

  return (
    <main className="bg-muted grid min-h-screen place-items-center p-6">
      <div className="mx-auto max-w-2xl">
        <h1 className="text-darkestPurple mb-6 text-center text-2xl font-semibold">
          Waterlily Planning
        </h1>

        <div className="bg-background shadow-xs rounded-xl border p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-950">How your data is shared</h2>

          {isLoading && <p className="text-foreground text-sm">Loading…</p>}

          {isError && (
            <p className="text-destructive text-sm">
              We couldn’t load this information right now. Please check back shortly.
            </p>
          )}

          {data && data.state === 'no-partners' && (
            <div className="text-foreground space-y-4 text-sm leading-relaxed">
              <p>
                {legalName} does not currently work with any outside partners on Waterlily, so your
                data is not shared with any third party at this time.
              </p>
              <p>
                Waterlily is the shared platform where you and your advisor work from the same plan,
                and the information you provide is stored centrally so your advisor can support your
                plan.
              </p>
              <p>
                In the future, {brandName} may work with partners to help with areas such as
                long-term care planning, insurance quoting, or insurance applications. If that
                happens, this page will be updated to list those partners, and your data may be
                shared with whichever partners {brandName} is working with at that time to support
                your plan. You’re welcome to check back here periodically to see the current list.
              </p>
              <p>
                Have questions about how your data is used or shared? Your advisor can walk you
                through it.
              </p>
            </div>
          )}

          {data && data.state === 'has-partners' && (
            <div className="text-gray-foreground space-y-4 text-sm leading-relaxed">
              <p>
                {legalName} works with the partners below to help with your long-term care planning,
                insurance quoting, and insurance applications:
              </p>
              <ul className="list-disc space-y-1 pl-5 font-medium text-gray-950">
                {data.partnerOrgLegalNames.map(name => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
              <p>
                Waterlily is the shared platform where you, your advisor, and these partners work
                from the same plan. The information you provide is stored centrally so each partner
                can access what they need to support your plan, such as preparing a quote or
                processing an application.
              </p>
              <p>
                {legalName}’s partners may change over time. This page will be updated to reflect
                those changes, and your consent to data sharing continues to apply to whichever
                partners {brandName} is working with at any given time. You’re welcome to check back
                here periodically to see the current list.
              </p>
              <p>
                Have questions about what any of these partners do? Your advisor can walk you
                through their roles.
              </p>
            </div>
          )}

          {data && <Disclaimer legalName={legalName} />}
        </div>
      </div>
    </main>
  );
}
